import { useCallback, useEffect, useMemo, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useForm } from "react-hook-form";
import { processError } from "../../../../common/utils/base_error";
import { useTreinamentoFuncionarioCadastroFilters } from "../hooks/TreinamentoFuncionario_cadastro/filters";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { TreinamentoFuncionario } from "../../domain/models/treinamento_funcionario";
import { TreinamentoFuncionarioForm } from "../schemas/TreinamentoFuncionario_cadastro/treinamento_funcionario";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { defaultReportTreinamentoFuncionarioValues, ReportTreinamentoFuncionarioForm, ReportTreinamentoFuncionarioSchema } from "../schemas/TreinamentoFuncionario_cadastro/report_treinamento_funcionario";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { FuncionarioAssinaturaDropDown } from "../../domain/models/funcionario_assinatura_dropdown";

/**
 * LoadTreinamentoFuncionariosState
 */

type LoadTreinamentoFuncionariosState = {
  cargosIds?: number[] | null;
  centroCustoIds?: number[] | null;
};

/**
 * useTreinamentoFuncionarioCadastroViewModel
 */

export const useTreinamentoFuncionarioCadastroViewModel = () => {
  const {
    commons: {
      getCargosDropDownUseCase,
      getCentroCustoDropDownUseCase,
    },
    reports: {
      getReportTreinamentoFuncionarioUseCase,
    },
    rh: {
      getTreinamentosDropDownUseCase,
      getAllTreinamentoFuncionarioUseCase,
      saveTreinamentoFuncionarioUseCase,
      getAllTreinamentoFuncionarioDataUseCase,
      getAllTreinamentoFuncionarioAssinaturaUseCase,
      getAllTreinamentoFuncionarioInstrutorUseCase,
      getAllTreinamentoFuncionarioLocalUseCase,
      getFuncionarioAssinaturaDropDownUseCase,
    },
  } = useDependencyInjection();

  const debouncedRef = useCreateDebounceHandler(750);

  const { setPageSaving, pushErrorNotification, pushInfoNotification } = usePageLoader();

  const hookForm = useForm<TreinamentoFuncionarioForm>();
  const hookFormReport = useForm<ReportTreinamentoFuncionarioForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: defaultReportTreinamentoFuncionarioValues,
    resolver: zodResolver(ReportTreinamentoFuncionarioSchema),
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [openReportListaTreinamentoModal, setOpenReportListaTreinamentoModal] = useState(false);

  const [vincularAssinaturas, setVincularAssinaturas] = useState(false);

  const [leftList, setLeftList] = useState<TreinamentoFuncionario[]>([]);
  const [rightList, setRightList] = useState<TreinamentoFuncionario[]>([]);

  const [funcionariosAssinaturasDropdown, setFuncionariosAssinaturasDropdown] = useState<FuncionarioAssinaturaDropDown[]>();

  const filters = useTreinamentoFuncionarioCadastroFilters();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const [dataRealizacao, instrutor] = hookFormReport.watch(['dataRealizacao', 'instrutor']);
  const [
    instrutorAssinatura1,
    instrutorAssinatura2,
    instrutorAssinatura3,
  ] = hookForm.watch([
    'instrutorAssinatura1',
    'instrutorAssinatura2',
    'instrutorAssinatura3',
  ]);

  const selectedFuncionariosAssinaturasDropdown = useMemo(() => {
    const results: number[] = [];

    const hasInstrutorAssinatura1 = instrutorAssinatura1 !== null && instrutorAssinatura1 > 0;
    const hasInstrutorAssinatura2 = instrutorAssinatura2 !== null && instrutorAssinatura2 > 0;
    const hasInstrutorAssinatura3 = instrutorAssinatura3 !== null && instrutorAssinatura3 > 0;

    if (hasInstrutorAssinatura1) {
      results.push(instrutorAssinatura1);
    }
    if (hasInstrutorAssinatura2) {
      results.push(instrutorAssinatura2);
    }
    if (hasInstrutorAssinatura3) {
      results.push(instrutorAssinatura3);
    }
    return results;
  }, [
    instrutorAssinatura1,
    instrutorAssinatura2,
    instrutorAssinatura3,
  ]);

  /**
   * Funcoes de carregamento
   */

  const loadFuncionarioAssinaturaDropDown = useCallback(async () => {
    try {
      const results = await getFuncionarioAssinaturaDropDownUseCase
        .getFuncionarioAssinaturaDropDown();
      setFuncionariosAssinaturasDropdown(results);
    } catch (e: unknown) {
      setFuncionariosAssinaturasDropdown(undefined);
      throw new Error('Falha ao buscar assinaturas.');
    }
  }, [
    getFuncionarioAssinaturaDropDownUseCase,
  ]);

  const loadTreinamentosDropDown = useCallback(async () => {
    try {
      const treinamentos = await getTreinamentosDropDownUseCase.getTreinamentosDropDown();
      useTreinamentoFuncionarioCadastroFilters.getState().setTreinamentos(treinamentos);
    } catch (e: unknown) {
      throw e;
    }
  }, [
    getTreinamentosDropDownUseCase
  ]);

  const loadCargosDropDown = useCallback(async () => {
    try {
      const cargos = await getCargosDropDownUseCase.getCargosDropDown();
      useTreinamentoFuncionarioCadastroFilters.getState().setCargos(cargos);
    } catch (e: unknown) {
      throw e;
    }
  }, [
    getCargosDropDownUseCase,
  ]);

  const loadCentroCustosDropDown = useCallback(async () => {
    try {
      const centroCustos = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      useTreinamentoFuncionarioCadastroFilters.getState().setCentroCustos(centroCustos);
    } catch (e: unknown) {
      throw e;
    }
  }, [
    getCentroCustoDropDownUseCase
  ]);

  const loadTreinamentoFuncionarios = useCallback(async (funcState?: LoadTreinamentoFuncionariosState) => {
    try {
      const filteredCargos = evaluateParamValue(funcState?.cargosIds, filters.selectedCargoIds);
      const filteredCentroCustos = evaluateParamValue(funcState?.centroCustoIds, filters.selectedCentroCustoIds);

      const treinamentoFuncionarios = await getAllTreinamentoFuncionarioUseCase.getAllTreinamentoFuncionario(
        filteredCargos ?? [],
        filteredCentroCustos ?? [],
      );

      setLeftList([...treinamentoFuncionarios]);
      setRightList([]);
    } catch (e: unknown) {
      setLeftList([]);
      setRightList([]);

      throw e;
    }
  }, [
    filters,
    getAllTreinamentoFuncionarioUseCase,
  ]);

  const loadTreinamentoFuncionarioData = useCallback(async () => {
    try {
      const datas = await getAllTreinamentoFuncionarioDataUseCase.getAllTreinamentoFuncionarioData();
      useTreinamentoFuncionarioCadastroFilters.getState().setDatas(datas);
    } catch (e: unknown) {
      useTreinamentoFuncionarioCadastroFilters.getState().setDatas([]);
      throw e;
    }
  }, [
    getAllTreinamentoFuncionarioDataUseCase,
  ]);

  const loadTreinamentoFuncionarioAssinatura = useCallback(async () => {
    try {
      const assinaturas = await getAllTreinamentoFuncionarioAssinaturaUseCase.getAllTreinamentoFuncionarioAssinatura();
      useTreinamentoFuncionarioCadastroFilters.getState().setAssinaturas(assinaturas);
    } catch (e: unknown) {
      useTreinamentoFuncionarioCadastroFilters.getState().setAssinaturas([]);
      throw e;
    }
  }, [
    getAllTreinamentoFuncionarioAssinaturaUseCase,
  ]);

  const loadTreinamentoFuncionarioInstrutores = useCallback(async (data: Date) => {
    try {
      const instrutores = await getAllTreinamentoFuncionarioInstrutorUseCase
        .getAllTreinamentoFuncionarioInstrutor(data);
      useTreinamentoFuncionarioCadastroFilters.getState().setInstrutores(instrutores);
    } catch (e: unknown) {
      useTreinamentoFuncionarioCadastroFilters.getState().setInstrutores([]);
      throw e;
    }
  }, [
    getAllTreinamentoFuncionarioInstrutorUseCase,
  ]);

  const loadTreinamentoFuncionarioLocais = useCallback(async (data: Date, instrutor: string) => {
    try {
      const locais = await getAllTreinamentoFuncionarioLocalUseCase
        .getAllTreinamentoFuncionarioLocal(data, instrutor);
      useTreinamentoFuncionarioCadastroFilters.getState().setLocais(locais);
    } catch (e: unknown) {
      useTreinamentoFuncionarioCadastroFilters.getState().setLocais([]);
      throw e;
    }
  }, [
    getAllTreinamentoFuncionarioLocalUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined);
      await loadTreinamentosDropDown();
      await loadCargosDropDown();
      await loadCentroCustosDropDown();
      await loadFuncionarioAssinaturaDropDown();
      await loadTreinamentoFuncionarios();
      await loadTreinamentoFuncionarioData();
      await loadTreinamentoFuncionarioAssinatura();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao iniciar pagina!")[0]);
    }
  });

  useEffect(() => {
    (async function () {
      try {
        if (dataRealizacao) {
          await loadTreinamentoFuncionarioInstrutores(dataRealizacao);
        } else {
          useTreinamentoFuncionarioCadastroFilters.getState().setInstrutores([]);
          useTreinamentoFuncionarioCadastroFilters.getState().setLocais([]);
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar instrutores!'));
      }
    })();
  }, [
    dataRealizacao,
    loadTreinamentoFuncionarioInstrutores,
    pushErrorNotification,
  ]);

  useEffect(() => {
    (async function () {
      try {
        if (dataRealizacao && instrutor) {
          await loadTreinamentoFuncionarioLocais(dataRealizacao, instrutor);
        } else {
          useTreinamentoFuncionarioCadastroFilters.getState().setLocais([]);
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar instrutores!'));
      }
    })();
  }, [
    dataRealizacao,
    instrutor,
    loadTreinamentoFuncionarioLocais,
    pushErrorNotification,
  ]);

  /**
   * Eventos
   */

  const handleReloadPage = useCallback(async () => {
    try {
      setLoading(true);
      await loadTreinamentosDropDown();
      await loadCargosDropDown();
      await loadCentroCustosDropDown();
      await loadFuncionarioAssinaturaDropDown();
      await loadTreinamentoFuncionarios();
      await loadTreinamentoFuncionarioData();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao iniciar pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadTreinamentosDropDown,
    loadCargosDropDown,
    loadCentroCustosDropDown,
    loadFuncionarioAssinaturaDropDown,
    loadTreinamentoFuncionarios,
    loadTreinamentoFuncionarioData,
    pushErrorNotification,
  ]);

  const handleSubmit = useCallback(async (form: TreinamentoFuncionarioForm) => {
    if (!saving || !loading) {
      try {
        setLoading(true);
        setPageSaving(true);

        await saveTreinamentoFuncionarioUseCase.saveTreinamentoFuncionario(
          form.treinamentoIds,
          form.funcionarioIds,
          form.dataRealizacao,
          form.local,
          form.instrutor,
          form.prazoAvaliacao,
          form.instrutorAssinatura1,
          form.instrutorAssinatura2,
          form.instrutorAssinatura3,
        );

        await loadTreinamentoFuncionarios();
        await loadTreinamentoFuncionarioData();

        pushInfoNotification("Sucesso", "Alterações salvas com sucesso!", { isAutoFinish: true });

        hookForm.reset();
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao iniciar pagina!"));
      } finally {
        setLoading(false);
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    saving,
    loading,
    hookForm,
    saveTreinamentoFuncionarioUseCase,
    loadTreinamentoFuncionarios,
    loadTreinamentoFuncionarioData,
    setPageSaving,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleSubmitReport = useCallback(async (form: ReportTreinamentoFuncionarioForm) => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportTreinamentoFuncionarioUseCase.getReportTreinamentoFuncionario(
            requiredMimeType,
            form.dataRealizacao,
            form.local,
            form.instrutor,
            form.conteudoProgramatico,
            form.assinatura,
          ),
        closeReportModalCallback: () => {
          setOpenReportListaTreinamentoModal(false);
        }
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao gerar relatorio"));
    } finally {
      setLoading(false);
    }
  }, [
    getReportTreinamentoFuncionarioUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleFuncionarioSelectionChange = useCallback((src: TreinamentoFuncionario[], dst: TreinamentoFuncionario[]) => {
    setLeftList(src);
    setRightList(dst);

    hookForm.setValue('funcionarioIds', dst.map(x => x.idFuncionario));
  }, [
    hookForm,
  ]);

  const handleSelectedCargosChange = useDebouncedCallback(debouncedRef, (cargos: number[] | null) => {
    useTreinamentoFuncionarioCadastroFilters.getState().setSelectedCargoIds(cargos);
    return async () => {
      try {
        setLoading(true);
        await loadTreinamentoFuncionarios({ cargosIds: cargos });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao iniciar pagina!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadTreinamentoFuncionarios,
    pushErrorNotification,
  ]);

  const handleSelectedCentroCustosChange = useDebouncedCallback(debouncedRef, (centroCustos: number[] | null) => {
    useTreinamentoFuncionarioCadastroFilters.getState().setSelectedCentroCustoIds(centroCustos);
    return async () => {
      try {
        setLoading(true);
        await loadTreinamentoFuncionarios({ centroCustoIds: centroCustos });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao iniciar pagina!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadTreinamentoFuncionarios,
    pushErrorNotification,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);

      useTreinamentoFuncionarioCadastroFilters.getState().setSelectedCargoIds(null);
      useTreinamentoFuncionarioCadastroFilters.getState().setSelectedCentroCustoIds(null);

      await loadTreinamentoFuncionarios({ cargosIds: null, centroCustoIds: null });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao iniciar pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadTreinamentoFuncionarios,
    pushErrorNotification,
  ]);

  const handleReportModalOpenState = useCallback((openState: boolean) => {
    setOpenReportListaTreinamentoModal(openState);
  }, []);

  const handleVincularAssinaturasChange = useCallback((value: boolean) => {
    setVincularAssinaturas(value);
  }, [
  ]);

  return {
    hookForm,
    hookFormReport,
    loading,
    saving,
    errorMessage,
    openReportListaTreinamentoModal,
    vincularAssinaturas,
    leftList: leftList.sort((a, b) => a.nome.toUpperCase().localeCompare(b.nome.toUpperCase())),
    rightList: rightList.sort((a, b) => a.nome.toUpperCase().localeCompare(b.nome.toUpperCase())),
    funcionariosAssinaturasDropdown: funcionariosAssinaturasDropdown ?? [],
    selectedFuncionariosAssinaturasDropdown,
    filters,
    reportViewerState,
    handleReloadPage,
    handleSubmit: hookForm.handleSubmit(handleSubmit),
    handleSubmitReport,
    handleFuncionarioSelectionChange,
    handleSelectedCargosChange,
    handleSelectedCentroCustosChange,
    handleClearFilters,
    handleReportModalOpenState,
    handleVincularAssinaturasChange,
  };
}